import querystring from 'query-string';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { fetchProductsByQuery as fetchProductsByQueryAction } from '../../../actions/product.actions';
import { dashboardCardDimensions } from '../../../assets/styles/themes/tokens';
import { CAROUSEL_DEFAULT_QUERY_OPTIONS, numberOfSlides, routes } from '../../../constants';
import {
	getProductsBySubjectId,
	getProductsBySubjectIdStatus,
} from '../../../reducers/dashboard.selectors';
import { selectUser } from '../../../reducers/user.selectors';
import { type SearchFilters, SearchResultsTab } from '../../../types/search';
import useAction from '../../../utils/useAction';
import useRouter from '../../../utils/useRouter';
import CardSkeleton from '../../Carousel/CardSkeleton';
import NewCarousel from '../../Carousel/NewCarousel';
import DashboardMuiCard from '../../cards/MuiCard/DashboardMuiCard';
import { getCommonCardProps } from '../../Search/SearchResults/SearchResults';

const StudyflixCarousel: React.FC = () => {
	const { t } = useTranslation();
	const { router } = useRouter();
	const slides = new Array(numberOfSlides.xl).fill(null);
	const user = useSelector(selectUser);
	const keyPrefix: string = 'Studyflix';
	const userSubjects = user?.subjectsFlattened || [];
	const subjectQueryParameter: string = userSubjects.map((sub) => sub.id).join(',');
	const loadingStatus = useSelector((state) =>
		getProductsBySubjectIdStatus(state, subjectQueryParameter, keyPrefix)
	);
	const fetchProductsByQuery = useAction(fetchProductsByQueryAction);
	const products = useSelector((state) =>
		getProductsBySubjectId(state, subjectQueryParameter, keyPrefix)
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!user) {
			return;
		}
		fetchProductsByQuery(
			{
				includePublishers: 'Studyflix',
				subjects: userSubjects.map((sub) => sub.id),
				keyPrefix,
			},
			CAROUSEL_DEFAULT_QUERY_OPTIONS
		);
	}, [user?.subjects]);

	if (products.length < numberOfSlides.xl) return null;

	const onShowAllClick = () => {
		const searchParams: SearchFilters = {
			sortBy: 'createdDate',
			publisher: ['Studyflix'],
		};

		const qs = querystring.stringify(searchParams, { skipNull: true });
		const uri = `/${routes.search.root}/${SearchResultsTab.PRODUCTS}?${qs}`;

		router.push(uri);
	};

	return (
		<NewCarousel
			title={t('Videos from Studyflix')}
			onShowAll={onShowAllClick}
			cardWidth={dashboardCardDimensions.video.width}
		>
			{loadingStatus === AsyncActionStatus.READY
				? products.map((product) => (
						<DashboardMuiCard
							key={product.id}
							contentType={'product'}
							{...getCommonCardProps({ ...product, type: 'product' })}
							isVideo
							redirectToMindItemId={product.mindItems[0]}
						/>
					))
				: // biome-ignore lint/suspicious/noArrayIndexKey: index keys are pre-existing; switching the key source changes reconciliation behavior
					slides.map((_, index) => <CardSkeleton key={index} />)}
		</NewCarousel>
	);
};

export default React.memo(StudyflixCarousel);
